import React from 'react'
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import ReactDOM from 'react-dom/client'
import './index.scss'
import '@fontsource/inter';
import { CssVarsProvider } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import { AuthContext, AuthProvider, RequireAuth, RequireGuest, useAuth } from '@providers/AuthProvider';
import { RouteObject, RouterProvider, createBrowserRouter, redirect, replace } from 'react-router-dom';
import AppLoading from 'routes/AppLoading';
import FramelessLayout from 'layouts/FramelessLayout';
import Login from 'routes/login/Login';
import LoginLayout from 'layouts/LoginLayout';
import theme, { colorSchemes } from 'theme';
import UnrecoverableError from '@components/UnrecoverableError';
import ForgotPassword from 'routes/login/ForgotPassword';
import LoggedInLayout from 'layouts/LoggedInLayout';
import Dashboard from 'routes/Dashboard';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authenticatedApi } from 'api';
import { GlobalStyles } from '@mui/joy';
import ContactIndex from 'routes/contacts/ContactIndex';
import WorkPlanIndex from 'routes/workplans/WorkPlanIndex';
import WorkPlanShow from 'routes/workplans/WorkPlanShow';
import BusinessIndex from 'routes/businesses/BusinessIndex';
import PropertyIndex from 'routes/properties/PropertyIndex';
import EventIndex from 'routes/events/EventIndex';
import ContactShow from 'routes/contacts/ContactShow';
import { queryClient } from '@api/queryClient';
import { Toaster } from 'react-hot-toast';
import BusinessShow from 'routes/businesses/BusinessShow';
import PropertyShow from 'routes/properties/PropertyShow';
import EventShow from 'routes/events/EventShow';
import OpportunityIndex from 'routes/opportunities/OpportunityIndex';
import DonationIndex from 'routes/donations/DonationIndex';
import OpportunityShow from 'routes/opportunities/OpportunityShow';
import DonationShow from 'routes/donations/DonationShow';
import ContactEdit from 'routes/contacts/ContactEdit';
// import MaestroLoader from '@components/MaestroLoader';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import ContactCreate from 'routes/contacts/ContactCreate';
import BusinessCreate from 'routes/businesses/BusinessCreate';
import BusinessEdit from 'routes/businesses/BusinessEdit';
import PropertyEdit from 'routes/properties/PropertyEdit';
import PropertyCreate from 'routes/properties/PropertyCreate';
import EventEdit from 'routes/events/EventEdit';
import EventCreate from 'routes/events/EventCreate';
import WorkPlanEdit from 'routes/workplans/WorkPlanEdit';
import WorkPlanCreate from 'routes/workplans/WorkPlanCreate';
import Settings from 'routes/settings/Settings';
import PublicImprovements from 'routes/settings/PublicImprovements';
import MembershipLevels from 'routes/settings/MembershipLevels';
import Trainings from 'routes/settings/Trainings';
import Appeals from 'routes/settings/Appeals';
import Campaigns from 'routes/settings/Campaigns';
import Funds from 'routes/settings/Funds';
import GrantCategories from 'routes/settings/GrantCategories';
import InvolvemnetCategories from 'routes/settings/InvolvementCategories';
import Involvements from 'routes/settings/Involvements';
import PublicInvestments from 'routes/settings/PublicInvestments';
import Parks from 'routes/settings/Parks';
import PublicParking from 'routes/settings/PublicParking';
import Registries from 'routes/settings/Registries';
import Zoning from 'routes/settings/Zoning';
import Ssas from 'routes/settings/Ssas';
import Tifs from 'routes/settings/Tifs';
import Grants from 'routes/settings/Grants';
import Users from 'settings/Users';
import OpportunityEdit from 'routes/opportunities/OpportunityEdit';
import OpportunityCreate from 'routes/opportunities/OpportunityCreate';
import DonationEdit from 'routes/donations/DonationEdit';
import DonationCreate from 'routes/donations/DonationCreate';
import Profile from 'routes/Profile';
import OrganizationSettings from 'settings/OrganizationSettings';
import { TenantProvider } from '@providers/TenantProvider';
import EnsureValidTenant from 'routes/EnsureValidTenant';
import VolunteerHoursBulkAdd from 'settings/VolunteerHoursBulkAdd';
import StatWidgetGenerator from 'settings/StatWidgetGenerator';
import VolunteerSignup from 'routes/login/VolunteerSignup';
import ImportIndex from 'routes/settings/ImportIndex';
import ImportShow from 'routes/settings/ImportShow';
import SettingsIndex from 'routes/settings/SettingsIndex';
import ImportCreate from 'settings/ImportCreate';
import SavedReports from 'routes/reports/SavedReports';
import NewReport from 'routes/reports/NewReport';
import ReportIndex from 'routes/reports/ReportIndex';
import { DashboardProvider } from '@providers/DashboardProvider';
import { isConductor } from '@utils/tenant';
import LoggedInConductorLayout from 'layouts/LoggedInConductorLayout';
import RapidReinvestment from 'routes/settings/RapidReinvestment';
import AuditLogIndex from "./routes/audit-log/AuditLogIndex";

const isSentryEnabled = !import.meta.env.DEV;

Sentry.init({
  dsn: "https://5bf66042e03c6b7687e5e0c7f9cfca26@o4505840916824064.ingest.us.sentry.io/4507964692299776",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  enabled: isSentryEnabled,
});

ReactGA.initialize("G-1WNF0KC06T");

type CrudScreen = any;// React.FC | ;

type CrudScreens = {
  index: CrudScreen,
  show: CrudScreen,
  create: CrudScreen,
  edit: CrudScreen,
};

type ICrud = {
  index?: boolean
  path?: string,
  element?: CrudScreen,
  errorElement?: any,
  children?: any,//Array<ICrud>,
} | RouteObject;// | RouteObject;

const crud = (object: string, screens: CrudScreens): ICrud => {
  return {
    path: `${object}`,
    // element: screens.index,
    children: [
      {
        index: true,
        element: screens.index,
        errorElement: <UnrecoverableError/>,
      },
      {
        path: `create`,
        element: screens.create,
        errorElement: <UnrecoverableError/>,
      },
      {
        path: `:id`,
        children: [
          {
            index: true,
            element: screens.show,
            errorElement: <UnrecoverableError/>,
          },
          {
            path: `edit`,
            element: screens.edit,
            errorElement: <UnrecoverableError/>,
          },
        ]
      },
    ]
  };
}

const router = createBrowserRouter([
  // Error Handler
  /*
  {
    path: '*',
    errorElement: <UnrecoverableError/>,
  },
  */

  /*
  |--------------------------------------------------------------------------
  | Loader
  |--------------------------------------------------------------------------
  */
  {
    path: "/",
    element: <AppLoading/>,
    errorElement: <UnrecoverableError/>,
    // loader: <MaestroLoader/>
  },

  /*
  |--------------------------------------------------------------------------
  | Unauthenticated Routes
  |--------------------------------------------------------------------------
  */
  {
    errorElement: <UnrecoverableError/>,
    element: <FramelessLayout/>,
    children: [
      {
        element: <LoginLayout/>,
        errorElement: <UnrecoverableError/>,
        children: [
          {
            path: "/login",
            element: <Login/>,
          },
          {
            path: "/forgot-password",
            children: [
              {
                index: true,
                element: <ForgotPassword/>,
              },
              {
                path: ":token",
                element: <p>set new password</p>,// <SetNewPassword/>,
              },
            ],
          },
        ]
      },

      {
        path: "/volunteer-signup/:uuid/:slug",
        element: <VolunteerSignup/>
      },
    ],
  },

  /*
  |--------------------------------------------------------------------------
  | Authenticated Routes
  |--------------------------------------------------------------------------
  */
  {
    errorElement: <UnrecoverableError/>,
    element: isConductor ? <LoggedInConductorLayout/> : <LoggedInLayout/>,
    children: [
      {
        path: "/dashboard",
        errorElement: <UnrecoverableError/>,
        element: <Dashboard />,
        children: [
          {
            path: "/dashboard/:id",
            errorElement: <UnrecoverableError/>,
            element: <Dashboard />,
          },
        ]
      },

      {
        path: "/reports/audit-log",
        errorElement: <UnrecoverableError/>,
        element: <AuditLogIndex/>,
      },

      crud('contacts', {
        index: <ContactIndex/>,
        show: <ContactShow/>,
        edit: <ContactEdit/>,
        create: <ContactCreate/>,
      }),

      crud('work-plans', {
        index: <WorkPlanIndex/>,
        show: <WorkPlanShow/>,
        edit: <WorkPlanEdit/>,
        create: <WorkPlanCreate/>,
      }),

      crud('businesses', {
        index: <BusinessIndex/>,
        show: <BusinessShow/>,
        edit: <BusinessEdit/>,
        create: <BusinessCreate/>,
      }),

      crud('properties', {
        index: <PropertyIndex/>,
        show: <PropertyShow/>,
        edit: <PropertyEdit/>,
        create: <PropertyCreate/>,
      }),

      crud('events', {
        index: <EventIndex/>,
        show: <EventShow/>,
        edit: <EventEdit/>,
        create: <EventCreate/>,
      }),

      crud('opportunities', {
        index: <OpportunityIndex/>,
        show: <OpportunityShow/>,
        edit: <OpportunityEdit/>,
        create: <OpportunityCreate/>,
      }),

      crud('donations', {
        index: <DonationIndex/>,
        show: <DonationShow/>,
        edit: <DonationEdit/>,
        create: <DonationCreate/>,
      }),

      {
        path: '/profile',
        element: <Profile/>,
      },

      /*
      {
        path: '/messages',
        children: [
          {
            index: true,
            element: <>messages</>,
          },
          {
            path: ':id',
            element: <>conversation</>,
          },
        ]
      },

      {
        path: '/notifications',
        element: <>notifications</>,
      },
      */

      // REPORTING ROUTES
      {
        // element: <ReportIndex/>,
        path: "/reports",
        errorElement: <UnrecoverableError/>,
        children: [
          {
            index: true,
            element: <ReportIndex/>,
            errorElement: <UnrecoverableError/>,
          },
          {
            path: "saved",
            element: <SavedReports/>,
            errorElement: <UnrecoverableError/>,
          },
          {
            path: "new",
            element: <NewReport/>,
            errorElement: <UnrecoverableError/>,
          },
        ],
      },

      // redirect settings to tools
      {
        path: "/settings",
        loader: async () => replace('/tools'),
        children: [
          {
            path: "/settings/*",
            index: true,
            loader: async ({ request }) => replace("/tools" + request.url.split('/settings')[1]),
          },
        ],
      },

      {
        element: <Settings/>,
        path: "/tools",
        children: [
          {
            index: true,
            element:  <SettingsIndex/>,
            errorElement: <UnrecoverableError/>,
          },
          // {
            // index: true,
            // element: <>dfdfaffafa</>,
            // errorElement: <UnrecoverableError/>,
            // children: [
              {
                path: "add-hours",
                element: <VolunteerHoursBulkAdd/>,
              },
              {
                path: "appeals",
                element: <Appeals/>,
              },
              {
                path: "campaigns",
                element: <Campaigns/>,
              },
              {
                path: "funds",
                element: <Funds/>,
              },
              {
                path: "grants/categories",
                element: <GrantCategories/>,
              },
              {
                path: "grants",
                element: <Grants/>,
              },
              {
                path: "involvements/categories",
                element: <InvolvemnetCategories/>,
              },
              {
                path: "involvements",
                element: <Involvements/>,
              },
              {
                path: "membership-levels",
                element: <MembershipLevels/>,
              },
              {
                path: "organization",
                loader: async () => replace('/tools/organization-settings'),
              },
              {
                path: "organization-settings",
                element: <OrganizationSettings/>,
              },
              {
                path: "public-improvements",
                element: <PublicImprovements/>,
              },
              {
                path: "public-investments",
                element: <PublicInvestments/>,
              },
              {
                path: "parks",
                element: <Parks/>,
              },
              {
                path: "public-parking",
                element: <PublicParking/>,
              },
              {
                path: "registries",
                element: <Registries/>,
              },
              {
                path: "ssas",
                element: <Ssas/>,
              },
              {
                path: "tifs",
                element: <Tifs/>,
              },
              {
                path: "trainings",
                element: <Trainings/>,
              },
              {
                path: "zoning",
                element: <Zoning/>,
              },
              {
                path: "users",
                element: <Users/>,
              },
              {
                path: "widget-generator",
                element: <StatWidgetGenerator/>,
              },

              {
                path: `/tools/importers`,
                children: [
                  {
                    index: true,
                    element: <ImportIndex/>,
                  },
                  {
                    path: `create`,
                    element: <ImportCreate/>,
                  },
                  {
                    path: `:id`,
                    children: [
                      {
                        index: true,
                        element: <ImportShow/>,
                        // errorElement: <UnrecoverableError/>,
                      },
                      /*
                      {
                        path: `edit`,
                        element: screens.edit,
                        errorElement: <UnrecoverableError/>,
                      },
                      */
                    ]
                  },
                ]
              },

              {
                path: "rapid-reinvestment",
                element: <RapidReinvestment/>,
              },
            ],
          // },
        // ],



      },
    ]
  },

]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <CssVarsProvider defaultMode="light" theme={theme} disableTransitionOnChange>
      <CssBaseline />
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--Header-height': '64px',
            /*
            '--Header-height': '52px',
            [theme.breakpoints.up('lg')]: {
              '--Header-height': '64px',
            },
            */
          },
        })}
      />
      <QueryClientProvider client={queryClient}>
        <TenantProvider>
          <AuthProvider>
            <DashboardProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <EnsureValidTenant>
                  <RouterProvider router={router}/>
                </EnsureValidTenant>
              </LocalizationProvider>
            </DashboardProvider>
          </AuthProvider>
        </TenantProvider>
      </QueryClientProvider>
      <Toaster
        position='bottom-right'
        reverseOrder={true}
        toastOptions={{
          success: {
            style: {
              background: 'var(--joy-palette-success-50)',
              color: 'var(--joy-palette-success-700)',
            },
            iconTheme: {
              primary: 'var(--joy-palette-success-500)',
              secondary: 'var(--joy-palette-success-50)',
            },
          },
          error: {
            style: {
              background: 'var(--joy-palette-danger-50)',
              color: 'var(--joy-palette-danger-700)',
            },
            iconTheme: {
              primary: 'var(--joy-palette-danger-500)',
              secondary: 'var(--joy-palette-danger-50)',
            },
          },
        }}
      />
    </CssVarsProvider>
  </React.StrictMode>
)
